import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Code2, Download,
  ExternalLink, Github, Play, Smartphone
} from "lucide-react";
import { Button } from "./components/ui/Button";
import ProjectMediaPlaceholder from "./components/ProjectMediaPlaceholder";
import { allCaseStudyProjects } from "./portfolioData";

function ProjectIcon({ type }) {
  return type === "mobile" ? <Smartphone className="h-6 w-6" /> : <Code2 className="h-6 w-6" />;
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const project = location.state?.project || allCaseStudyProjects.find((item) => item.id === id);
  const [currentImage, setCurrentImage] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = project
      ? `${project.title} | Mohamad Sallah`
      : "Project not found | Mohamad Sallah";
    return () => { document.title = "Mohamad Sallah - AI Application & Software Engineer | Dubai, UAE"; };
  }, [project]);

  useEffect(() => {
    const elements = document.querySelectorAll("[data-detail-reveal]");
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -6%", threshold: 0.06 });
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [project]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  if (!project) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 px-4 text-white">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Project not found</h1>
          <p className="mt-3 text-slate-400">This project link is unavailable or has changed.</p>
          <Button onClick={() => navigate("/")} className="primary-action mt-6"><ArrowLeft className="mr-2 h-4 w-4" />Back to portfolio</Button>
        </div>
      </main>
    );
  }

  const images = [project.coverImage, ...(project.media || [])].filter(Boolean);
  const links = project.links || {};
  const previousImage = () => { setImageFailed(false); setImageLoaded(false); setCurrentImage((value) => (value - 1 + images.length) % images.length); };
  const nextImage = () => { setImageFailed(false); setImageLoaded(false); setCurrentImage((value) => (value + 1) % images.length); };

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/90 backdrop-blur-md">
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6" aria-label="Project navigation">
          <button type="button" onClick={goBack} className="inline-flex items-center text-sm text-slate-300 transition-colors duration-200 hover:text-cyan-300"><ArrowLeft className="mr-2 h-4 w-4" />Back</button>
          <span className="max-w-[60%] truncate font-semibold text-slate-100">{project.title}</span>
        </nav>
      </header>

      <section className="relative border-b border-slate-800 px-4 py-16 sm:px-6 sm:py-24">
        <div className="detail-header signature-accent mx-auto max-w-5xl" data-detail-reveal>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-cyan-500/25 bg-cyan-500/10 text-cyan-300"><ProjectIcon type={project.type} /></div>
          {project.eyebrow && <p className="mt-7 text-sm font-semibold uppercase tracking-[0.16em] text-cyan-400">{project.eyebrow}</p>}
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-6xl">{project.title}</h1>
          <p className="mt-6 max-w-4xl text-lg leading-8 text-slate-300">{project.description}</p>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
            <div data-detail-reveal>
              <h2 className="text-2xl font-bold sm:text-3xl">Engineering scope</h2>
              <p className="mt-5 leading-8 text-slate-300">{project.fullDescription}</p>
              {project.architecture && <><h3 className="mt-9 text-sm font-semibold uppercase tracking-[0.15em] text-cyan-400">Architecture workflow</h3><p className="mt-4 leading-8 text-slate-300">{project.architecture}</p></>}
              {project.capabilities?.length > 0 && <><h3 className="mt-9 text-sm font-semibold uppercase tracking-[0.15em] text-cyan-400">Repository-verified capabilities</h3><ul className="mt-4 space-y-3 text-slate-300">{project.capabilities.map((capability) => <li key={capability} className="flex gap-3"><span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" /><span className="leading-7">{capability}</span></li>)}</ul></>}
              <h3 className="mt-9 text-sm font-semibold uppercase tracking-[0.15em] text-cyan-400">Technologies and systems</h3>
              <div className="mt-4 flex flex-wrap gap-2">{project.technologies.map((technology) => <span key={technology} className="tech-tag">{technology}</span>)}</div>
            </div>

            <aside className="surface-card h-fit" data-detail-reveal>
              <h2 className="text-lg font-bold">Project evidence</h2>
              <p className="mt-2 text-sm leading-6 text-slate-400">Available product, code, and demonstration links.</p>
              <div className="mt-6 flex flex-col gap-3">
                {links.website && <a href={links.website} target="_blank" rel="noreferrer" className="secondary-action"><ExternalLink className="mr-2 h-4 w-4" />Official Website</a>}
                {links.appStore && <a href={links.appStore} target="_blank" rel="noreferrer" className="secondary-action"><Download className="mr-2 h-4 w-4" />App Store</a>}
                {links.playStore && <a href={links.playStore} target="_blank" rel="noreferrer" className="secondary-action"><Download className="mr-2 h-4 w-4" />Google Play</a>}
                {links.github && <a href={links.github} target="_blank" rel="noreferrer" className="secondary-action"><Github className="mr-2 h-4 w-4" />GitHub</a>}
                {links.demo && <a href={links.demo} target="_blank" rel="noreferrer" className="secondary-action"><ExternalLink className="mr-2 h-4 w-4" />View Demo</a>}
                {links.download && <a href={links.download} target="_blank" rel="noreferrer" className="secondary-action"><Download className="mr-2 h-4 w-4" />Download Build</a>}
                {links.video && <a href={links.video} target="_blank" rel="noreferrer" className="secondary-action"><Play className="mr-2 h-4 w-4" />View Video</a>}
                {!Object.values(links).some(Boolean) && <p className="rounded-lg border border-dashed border-slate-700 p-4 text-sm text-slate-500">Public evidence is not yet available for this project.</p>}
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-800 bg-slate-900/30 px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto max-w-5xl" data-detail-reveal>
          <div className="mb-7"><p className="text-sm font-semibold uppercase tracking-[0.15em] text-cyan-400">Product media</p><h2 className="mt-3 text-2xl font-bold sm:text-3xl">Screenshots and interface evidence</h2></div>
          {images.length ? (
            <>
              <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-slate-800 bg-slate-950">
                {!imageFailed ? <img src={images[currentImage]} alt={`${project.title} screenshot ${currentImage + 1}`} width="1200" height="750" loading="eager" fetchPriority="high" decoding="async" onLoad={() => setImageLoaded(true)} onError={() => setImageFailed(true)} className={`media-image h-full w-full object-contain ${imageLoaded ? "is-loaded" : ""}`} /> : <ProjectMediaPlaceholder project={project} />}
                {images.length > 1 && <><button type="button" onClick={previousImage} aria-label="Previous screenshot" className="carousel-control left-3 sm:left-5"><ChevronLeft className="h-5 w-5" /></button><button type="button" onClick={nextImage} aria-label="Next screenshot" className="carousel-control right-3 sm:right-5"><ChevronRight className="h-5 w-5" /></button><span className="absolute right-4 top-4 rounded-md bg-slate-950/80 px-3 py-1.5 text-sm text-slate-300">{currentImage + 1}/{images.length}</span></>}
              </div>
              {images.length > 1 && <div className="mt-4 grid grid-cols-4 gap-3">{images.map((image, index) => <button type="button" key={image} onClick={() => { setImageFailed(false); setImageLoaded(false); setCurrentImage(index); }} aria-label={`View screenshot ${index + 1}`} aria-current={index === currentImage} className={`aspect-[16/10] overflow-hidden rounded-lg border bg-slate-950 transition-colors duration-200 ${index === currentImage ? "border-cyan-400" : "border-slate-700"}`}><img src={image} alt="" width="240" height="150" loading="lazy" decoding="async" onError={(event) => { event.currentTarget.style.display = "none"; }} className="h-full w-full object-cover" /></button>)}</div>}
            </>
          ) : (
            <div className="aspect-[16/7] overflow-hidden rounded-2xl border border-slate-800"><ProjectMediaPlaceholder project={project} /></div>
          )}
        </div>
      </section>

      <footer className="px-4 py-7 text-center text-sm text-slate-500">© {new Date().getFullYear()} Mohamad Sallah. AI Application &amp; Software Engineer - Dubai, UAE.</footer>
    </main>
  );
}
